import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 9,
    fontFamily: "Helvetica",
    color: "#334155",
  },
  brandBar: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 6,
    height: "100%",
    backgroundColor: "#0f172a", // Darker professional slate
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottom: "1pt solid #e2e8f0",
    paddingBottom: 15,
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0f172a",
    textTransform: "uppercase",
  },

  // Section Header Styling
  sectionHeader: {
    backgroundColor: "#f1f5f9",
    padding: "6 8",
    marginTop: 12,
    flexDirection: "row",
    borderLeft: "3pt solid #0f172a",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#0f172a",
    textTransform: "uppercase",
  },

  // Sub-header Styling
  subHeader: {
    padding: "4 8",
    marginTop: 4,
    backgroundColor: "#ffffff",
    borderBottom: "0.5pt solid #cbd5e1",
  },
  subHeaderTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#334155",
    fontStyle: "italic",
  },

  // Line Item Row
  itemRow: {
    flexDirection: "row",
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottom: "0.2pt solid #e2e8f0",
  },

  // Table Columns
  colDesc: { width: "55%" },
  colQty: { width: "10%", textAlign: "center" },
  colUnit: { width: "15%", textAlign: "right" },
  colTotal: { width: "20%", textAlign: "right" },

  totalsContainer: {
    marginTop: 20,
    alignSelf: "flex-end",
    width: "35%",
    borderTop: "1pt solid #0f172a",
    paddingTop: 8,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  grandTotal: {
    marginTop: 5,
    paddingTop: 5,
    borderTop: "1pt solid #e2e8f0",
    fontWeight: "bold",
    fontSize: 11,
    color: "#0f172a",
  },
});

const QuotationPdfDocument = ({
  items = [],
  customerDetails = {},
  quotationDetails = {},
}) => {
  const formatCurrency = (val) =>
    val.toLocaleString("en-AE", { minimumFractionDigits: 2 });

  // Calculate Grand Total
  const grandTotal = items
    .filter((item) => item.level === 3)
    .reduce((sum, item) => sum + item.qty * item.tenderRate, 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.brandBar} />

        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.mainTitle}>Quotation</Text>
            <Text style={{ marginTop: 4, color: "#64748b" }}>
              Ref: {quotationDetails.enquiryNo}
            </Text>
          </View>
          <View style={{ textAlign: "right" }}>
            <Text style={{ fontWeight: "bold" }}>
              {customerDetails.customerName}
            </Text>
            <Text style={{ fontSize: 8, color: "#64748b" }}>
              {customerDetails.address}
            </Text>
          </View>
        </View>

        {/* Column Labels */}
        <View
          style={{
            flexDirection: "row",
            paddingHorizontal: 8,
            marginBottom: 5,
            opacity: 0.6,
          }}
        >
          <Text style={styles.colDesc}>Work Description</Text>
          <Text style={styles.colQty}>Qty</Text>
          <Text style={styles.colUnit}>Unit Rate</Text>
          <Text style={styles.colTotal}>Amount</Text>
        </View>

        {/* Structured Items Rendering */}
        {items.map((item, index) => {
          if (item.level === 1) {
            // RENDER HEADER
            return (
              <View key={index} style={styles.sectionHeader} wrap={false}>
                <Text style={styles.sectionTitle}>{item.label}</Text>
              </View>
            );
          }

          if (item.level === 2) {
            // RENDER SUB-HEADER
            return (
              <View key={index} style={styles.subHeader}>
                <Text style={styles.subHeaderTitle}>{item.label}</Text>
              </View>
            );
          }

          if (item.level === 3) {
            // RENDER ACTUAL ITEM
            return (
              <View key={index} style={styles.itemRow}>
                <Text style={styles.colDesc}>• {item.label}</Text>
                <Text style={styles.colQty}>{item.qty}</Text>
                <Text style={styles.colUnit}>
                  {formatCurrency(item.tenderRate)}
                </Text>
                <Text style={styles.colTotal}>
                  {formatCurrency(item.qty * item.tenderRate)}
                </Text>
              </View>
            );
          }
          return null;
        })}

        {/* Totals Section */}
        <View style={styles.totalsContainer} wrap={false}>
          <View style={styles.totalRow}>
            <Text style={{ color: "#64748b" }}>Net Amount:</Text>
            <Text>{formatCurrency(grandTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={{ color: "#64748b" }}>VAT (5%):</Text>
            <Text>{formatCurrency(grandTotal * 0.05)}</Text>
          </View>
          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text>Total AED:</Text>
            <Text>{formatCurrency(grandTotal * 1.05)}</Text>
          </View>
        </View>

        {/* Footer */}
        <View
          style={{
            marginTop: 40,
            borderTop: "0.5pt solid #e2e8f0",
            paddingTop: 10,
          }}
        >
          <Text style={{ fontSize: 8, fontWeight: "bold" }}>
            Terms & Conditions:
          </Text>
          <Text style={{ fontSize: 7, color: "#94a3b8", marginTop: 4 }}>
            1. This quotation is valid for 30 days. 2. Payment terms as per
            contract agreement.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default QuotationPdfDocument;
